<template>
	<teleport to="body">
		<transition name="chat-drawer">
			<chat-drawer v-if="drawerShown" :loading="listLoading" :load-failed="listFailed" @close="closeDrawer" />
		</transition>
		<div v-if="popups.length" class="chat-dock" :class="{ 'chat-dock--beside-drawer': drawerShown }">
			<chat-window v-for="popup in popups" :key="popup.key" :popup="popup" />
		</div>
		<chat-rate-dialog />
		<transition-group
			tag="div"
			name="chat-toast"
			class="chat-toasts"
			:class="{ 'chat-toasts--beside-drawer': drawerShown }"
			aria-live="polite"
		>
			<chat-toast
				v-for="toast in toasts"
				:key="toast.id"
				:toast="toast"
				@open="openFromToast"
				@dismiss="dismissToast"
			/>
		</transition-group>
	</teleport>
</template>

<script>
	import { Howl } from 'howler';
	import { mapGetters } from 'vuex';
	import { chatChannelLabelKey, chatDisplayName } from '@/services/chat/channels';
	import { isMine, watchOpenChats } from '@/services/chat/chatFirestore';
	import { retryDelay } from '@/services/chat/chatSession';
	import {
		askToRateChat,
		chatPhase,
		chatStore,
		consumePendingOpen,
		hasSeveralRestaurants,
		isChatWindowFocused,
		openChat,
		setOpenChats,
		toggleDrawer,
	} from '@/services/chat/chatStore';
	import ChatDrawer from './ChatDrawer.vue';
	import ChatRateDialog from './ChatRateDialog.vue';
	import ChatToast from './ChatToast.vue';
	import ChatWindow from './ChatWindow.vue';
	import { chatToasts, clearChatToasts, dismissChatToast, showChatToast } from './chatToasts';

	const SOUND_GAP_MS = 2000;

	const noop = () => undefined;

	const chatRef = (chat) => `${chat.typeId}:${chat.chatId}`;

	export default {
		name: 'ChatHost',
		components: { ChatDrawer, ChatRateDialog, ChatToast, ChatWindow },
		data: () => ({
			listLoading: false,
			listFailed: false,
			toasts: chatToasts,
		}),
		computed: {
			...mapGetters({
				user: 'user',
			}),
			phase() {
				return chatPhase();
			},
			businessCodes() {
				return chatStore.businessCodes;
			},
			drawerShown() {
				return chatStore.drawerOpen && this.phase !== 'off';
			},
			popups() {
				return chatStore.popups;
			},
			pendingOpen() {
				return [chatStore.pendingOpen, chatStore.businessCodes];
			},
		},
		watch: {
			businessCodes(codes) {
				this.startListening(codes);
			},
			pendingOpen() {
				consumePendingOpen();
			},
			phase(phase, previous) {
				if (previous === 'ready' || phase === 'off') {
					clearChatToasts();
				}
				if (phase === 'off') {
					toggleDrawer(false);
				}
			},
		},
		methods: {
			startListening(codes) {
				this.stopListening();
				this.known = null;
				if (!codes) {
					this.listLoading = false;
					this.listFailed = false;
					return;
				}
				this.listLoading = true;
				this.listFailed = false;
				const attach = () => {
					let delivered = false;
					this.stopWatch = watchOpenChats({
						businessCodes: codes,
						onChange: (rows, { complete }) => {
							delivered = true;
							if (!setOpenChats(rows, codes)) {
								return;
							}
							if (complete) {
								this.listLoading = false;
								this.listFailed = false;
								this.failures = 0;
							}
							this.alertIncoming(rows, complete);
						},
						onError: () => {
							if (delivered || this.retryTimer) {
								return;
							}
							this.listLoading = false;
							this.listFailed = true;
							const delay = retryDelay(this.failures);
							this.failures += 1;
							this.retryTimer = setTimeout(() => {
								this.retryTimer = null;
								this.stopWatch();
								attach();
							}, delay);
						},
					});
				};
				attach();
			},
			stopListening() {
				clearTimeout(this.retryTimer);
				this.retryTimer = null;
				this.stopWatch();
				this.stopWatch = noop;
				this.failures = 0;
			},
			alertIncoming(rows, complete) {
				const previous = this.known;
				if (!previous && !complete) {
					return;
				}
				this.known = new Map(
					rows.map((chat) => [
						chatRef(chat),
						{ unread: chat.unreadCount, last: chat.lastMessage ? chat.lastMessage.id : null },
					])
				);
				if (!previous) {
					return;
				}
				const fresh = rows.filter((chat) => {
					const before = previous.get(chatRef(chat));
					const last = chat.lastMessage;
					return (
						!!last &&
						!isMine(last) &&
						chat.unreadCount > (before ? before.unread : 0) &&
						(!before || before.last !== last.id)
					);
				});
				if (!fresh.length || document.visibilityState !== 'visible') {
					return;
				}
				const alerting = fresh.filter(
					(chat) => !isChatWindowFocused({ typeId: chat.typeId, chatId: chat.chatId })
				);
				if (!alerting.length) {
					return;
				}
				this.playSound();
				alerting.forEach((chat) => this.toastIncoming(chat));
			},
			toastIncoming(chat) {
				const named = !!chat.counterpart.name;
				const business = hasSeveralRestaurants(this.user) ? chat.businessName : '';
				showChatToast({
					kind: 'message',
					title: chatDisplayName(this.$t, chat.typeId, chat.counterpart),
					subtitle: [named ? this.$t(chatChannelLabelKey(chat.typeId)) : '', business]
						.filter(Boolean)
						.join(' · '),
					text: chat.lastMessage.text || this.$t('chat.list_photo'),
					typeId: chat.typeId,
					image: chat.counterpart.image,
					target: { typeId: chat.typeId, chatId: chat.chatId },
					group: chatRef(chat),
				});
			},
			alertPush(push) {
				if (push.closed) {
					askToRateChat({ typeId: push.typeId, chatId: push.chatId, message: push.body });
					return;
				}
				const listening = this.phase === 'ready' && !!this.known;
				if (this.phase === 'off' || listening || document.visibilityState !== 'visible') {
					return;
				}
				this.playSound();
				showChatToast({
					kind: 'message',
					title: push.title || this.$t(chatChannelLabelKey(push.typeId)),
					text: push.body || this.$t('chat.list_photo'),
					typeId: push.typeId,
					target: { typeId: push.typeId, chatId: push.chatId, key: push.key },
					group: `push:${push.typeId}:${push.chatId || push.key}`,
				});
			},
			playSound() {
				const now = Date.now();
				if (now - this.lastSoundAt < SOUND_GAP_MS) {
					return;
				}
				this.lastSoundAt = now;
				if (!this.sound) {
					this.sound = new Howl({ html5: true, src: ['audio/chat_notification.mp3'], volume: 1 });
				}
				this.sound.play();
			},
			openFromToast(toast) {
				dismissChatToast(toast.id);
				if (this.phase !== 'ready') {
					toggleDrawer(true);
					return;
				}
				if (!openChat(toast.target)) {
					showChatToast({ kind: 'error', text: this.$t('chat.unavailable') });
				}
			},
			dismissToast(toast) {
				dismissChatToast(toast.id);
			},
			closeDrawer() {
				toggleDrawer(false);
			},
		},
		created() {
			this.stopWatch = noop;
			this.retryTimer = null;
			this.failures = 0;
			this.known = null;
			this.sound = null;
			this.lastSoundAt = 0;
			this.startListening(chatStore.businessCodes);
			consumePendingOpen();
			this.emitter.on('chatPushReceived', this.alertPush);
		},
		beforeUnmount() {
			this.emitter.off('chatPushReceived', this.alertPush);
			this.stopListening();
			if (this.sound) {
				this.sound.unload();
				this.sound = null;
			}
			clearChatToasts();
		},
	};
</script>

<style lang="scss" scoped>
	$drawer-width: 384px;
	$phone: 760px;


	.chat-dock {
		position: fixed;
		right: 0;
		bottom: 0;
		z-index: 10001;
		display: flex;
		flex-direction: row-reverse;
		align-items: flex-end;
		gap: 8px;
		max-width: 100%;
		padding: 0 8px;
		overflow-x: auto;
		pointer-events: none;

		> * {
			pointer-events: auto;
		}
	}

	.chat-toasts {
		position: fixed;
		top: 72px;
		right: 16px;
		z-index: 10003;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 8px;
		pointer-events: none;
	}

	@media (min-width: $phone + 1) {
		.chat-dock--beside-drawer {
			right: $drawer-width;
			max-width: calc(100% - #{$drawer-width});
		}

		.chat-toasts--beside-drawer {
			right: $drawer-width + 16px;
		}
	}

	.chat-drawer-enter-active,
	.chat-drawer-leave-active {
		transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease;
	}

	.chat-drawer-enter-from,
	.chat-drawer-leave-to {
		transform: translateX(100%);
		opacity: 0.6;
	}

	.chat-toast-enter-active,
	.chat-toast-leave-active {
		transition: transform 0.2s ease, opacity 0.2s ease;
	}

	.chat-toast-enter-from,
	.chat-toast-leave-to {
		transform: translateX(24px);
		opacity: 0;
	}

	@media (prefers-reduced-motion: reduce) {
		.chat-drawer-enter-active,
		.chat-drawer-leave-active,
		.chat-toast-enter-active,
		.chat-toast-leave-active {
			transition: none;
		}
	}
</style>
